import { cn } from "./classNames-DkX30VJU.js";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
// src/components/Badge/BadgeAddon.tsx
const addonVariants = cva([
	"tw:flex",
	"tw:items-center",
	"tw:shrink-0",
	"tw:bg-inherit",
	"tw:self-stretch",
	"tw:px-1",
	"tw:has-[button]:px-0",
	"tw:[&_svg:not([class*=\"tw:size-\"])]:size-3"
], {
	defaultVariants: {
		align: "inline-end",
		variant: "inline"
	},
	variants: {
		align: {
			"inline-start": ["tw:order-first"],
			"inline-end": ["tw:order-last"]
		},
		variant: {
			inline: [],
			floating: [
				"tw:hidden",
				"tw:group-hover/badge:flex",
				"tw:absolute",
				"tw:top-0",
				"tw:bottom-0",
				"tw:focus-within:flex"
			]
		}
	},
	compoundVariants: [{
		variant: "floating",
		align: "inline-end",
		className: ["tw:right-0"]
	}, {
		variant: "floating",
		align: "inline-start",
		className: ["tw:left-0"]
	}]
});
const BadgeAddon = ({ ref, align = "inline-end", variant = "inline", className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	ref,
	"data-align": align,
	"data-variant": variant,
	className: cn(addonVariants({
		align,
		variant,
		className
	}))
});
// src/components/Badge/BadgeButton.tsx
const BadgeButton = ({ ref, className, type = "button", children, ...rest }) => /* @__PURE__ */ jsx("button", {
	...rest,
	ref,
	type,
	className: cn("tailwindcss-preflight", "tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:cursor-pointer", "tw:bg-inherit", "tw:text-inherit", "tw:hover:brightness-95", "tw:px-1", "tw:focus-visible:outline-hidden", "tw:focus-visible:ring-2", "tw:focus-visible:ring-inset", "tw:focus-visible:ring-ring", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-3", className),
	children
});
// src/components/Badge/BadgeLabel.tsx
const BadgeLabel = ({ ref, className, asChild = false, ...rest }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "span", {
		...rest,
		ref,
		"data-slot": "badge-label",
		className: cn("tw:max-w-56", "tw:truncate", "tw:px-2", "tw:py-0.5", "tw:group-has-[[data-align=inline-start][data-variant=inline]:not(:empty)]/badge:pl-0", "tw:group-has-[[data-align=inline-end][data-variant=inline]:not(:empty)]/badge:pr-0", className)
	});
};
const badgeRootVariants = cva([
	"tailwindcss-preflight",
	"tw:group/badge",
	"tw:relative",
	"tw:inline-flex",
	"tw:justify-center",
	"tw:items-center",
	"tw:min-h-5",
	"tw:h-fit",
	"tw:shrink-0",
	"tw:overflow-hidden",
	"tw:whitespace-pre-wrap",
	"tw:break-normal",
	"tw:text-xs",
	"tw:font-medium",
	"tw:tracking-chrome",
	"tw:text-center",
	"tw:underline",
	"tw:border-none",
	"tw:[&>svg]:self-center",
	"tw:[button&]:cursor-pointer",
	"tw:focus-visible:outline-hidden",
	"tw:focus-visible:ring-2",
	"tw:focus-visible:ring-inset",
	"tw:focus-visible:ring-ring"
], {
	defaultVariants: {
		color: "gray",
		variant: "default"
	},
	variants: {
		variant: {
			default: ["tw:rounded"],
			tag: ["tw:rounded tw:normal-case"],
			pill: ["tw:min-w-5 tw:rounded-full tw:normal-case tw:tracking-normal"]
		},
		color: {
			gray: "tw:bg-neutral-500 tw:text-white tw:decoration-neutral-500",
			"gray-darker": "tw:bg-neutral-600 tw:text-white tw:decoration-neutral-600",
			"gray-inverse": "tw:bg-neutral-100 tw:text-neutral-500 tw:decoration-neutral-100 tw:[[data-theme=dark]_&]:bg-neutral-700 tw:[[data-theme=dark]_&]:text-neutral-300 tw:[[data-theme=dark]_&]:decoration-neutral-700",
			green: "tw:bg-green-500 tw:text-white tw:decoration-green-500 tw:[[data-theme=dark]_&]:bg-green-800 tw:[[data-theme=dark]_&]:decoration-green-800",
			"green-darker": "tw:bg-green-600 tw:text-white tw:decoration-green-600 tw:[[data-theme=dark]_&]:bg-green-900 tw:[[data-theme=dark]_&]:decoration-green-900",
			"green-inverse": "tw:bg-green-100 tw:text-green-900 tw:decoration-green-100 tw:[[data-theme=dark]_&]:bg-green-900 tw:[[data-theme=dark]_&]:text-green-200 tw:[[data-theme=dark]_&]:decoration-green-900",
			blue: "tw:bg-blue-500 tw:text-white tw:decoration-blue-500 tw:[[data-theme=dark]_&]:bg-blue-600 tw:[[data-theme=dark]_&]:decoration-blue-600",
			"blue-inverse": "tw:bg-blue-100 tw:text-blue-800 tw:decoration-blue-100 tw:[[data-theme=dark]_&]:bg-blue-900 tw:[[data-theme=dark]_&]:text-blue-200 tw:[[data-theme=dark]_&]:decoration-blue-900",
			red: "tw:bg-error-500 tw:text-white tw:decoration-error-500 tw:[[data-theme=dark]_&]:bg-error-600 tw:[[data-theme=dark]_&]:decoration-error-600",
			"red-inverse": "tw:bg-error-100 tw:text-error-800 tw:decoration-error-100 tw:[[data-theme=dark]_&]:bg-error-900 tw:[[data-theme=dark]_&]:text-error-200 tw:[[data-theme=dark]_&]:decoration-error-900",
			orange: "tw:bg-warning-500 tw:text-white tw:decoration-warning-500 tw:[[data-theme=dark]_&]:bg-warning-700 tw:[[data-theme=dark]_&]:decoration-warning-700",
			"orange-inverse": "tw:bg-warning-100 tw:text-warning-800 tw:decoration-warning-100 tw:[[data-theme=dark]_&]:bg-warning-900 tw:[[data-theme=dark]_&]:text-warning-200 tw:[[data-theme=dark]_&]:decoration-warning-900",
			amber: "tw:bg-warning-300 tw:text-warning-800 tw:decoration-warning-300 tw:[[data-theme=dark]_&]:bg-warning-800 tw:[[data-theme=dark]_&]:text-warning-100 tw:[[data-theme=dark]_&]:decoration-warning-800",
			"amber-inverse": "tw:bg-warning-200 tw:text-warning-800 tw:decoration-warning-200 tw:[[data-theme=dark]_&]:bg-warning-800 tw:[[data-theme=dark]_&]:text-warning-200 tw:[[data-theme=dark]_&]:decoration-warning-800",
			teal: "tw:bg-teal-500 tw:text-white tw:decoration-teal-500 tw:[[data-theme=dark]_&]:bg-teal-700 tw:[[data-theme=dark]_&]:decoration-teal-700",
			"teal-inverse": "tw:bg-teal-100 tw:text-teal-800 tw:decoration-teal-100 tw:[[data-theme=dark]_&]:bg-teal-900 tw:[[data-theme=dark]_&]:text-teal-200 tw:[[data-theme=dark]_&]:decoration-teal-900",
			purple: "tw:bg-purple-500 tw:text-white tw:decoration-purple-500 tw:[[data-theme=dark]_&]:bg-purple-600 tw:[[data-theme=dark]_&]:decoration-purple-600",
			"purple-inverse": "tw:bg-purple-100 tw:text-purple-800 tw:decoration-purple-100 tw:[[data-theme=dark]_&]:bg-purple-900 tw:[[data-theme=dark]_&]:text-purple-200 tw:[[data-theme=dark]_&]:decoration-purple-900",
			cyan: "tw:bg-cyan-500 tw:text-white tw:decoration-cyan-500 tw:[[data-theme=dark]_&]:bg-cyan-700 tw:[[data-theme=dark]_&]:decoration-cyan-700",
			"cyan-inverse": "tw:bg-cyan-100 tw:text-cyan-800 tw:decoration-cyan-100 tw:[[data-theme=dark]_&]:bg-cyan-900 tw:[[data-theme=dark]_&]:text-cyan-200 tw:[[data-theme=dark]_&]:decoration-cyan-900",
			"sidebar-primary": "tw:bg-sidebar-primary tw:text-white tw:decoration-sidebar-primary",
			white: "tw:bg-white/90 tw:text-neutral-600 tw:decoration-white/90",
			success: "tw:bg-success tw:text-success-foreground tw:decoration-success tw:[[data-theme=dark]_&]:text-background",
			"success-subtle": "tw:bg-success-subtle tw:text-success-subtle-foreground tw:decoration-success-subtle",
			warning: "tw:bg-warning tw:text-warning-foreground tw:decoration-warning",
			"warning-subtle": "tw:bg-warning-subtle tw:text-warning-subtle-foreground tw:decoration-warning-subtle",
			info: "tw:bg-info tw:text-info-foreground tw:decoration-info tw:[[data-theme=dark]_&]:text-background",
			"info-subtle": "tw:bg-info-subtle tw:text-info-subtle-foreground tw:decoration-info-subtle",
			destructive: "tw:bg-destructive tw:text-destructive-foreground tw:decoration-destructive tw:[[data-theme=dark]_&]:text-background",
			"destructive-subtle": "tw:bg-destructive-subtle tw:text-destructive-subtle-foreground tw:decoration-destructive-subtle",
			neutral: "tw:bg-muted-foreground tw:text-background tw:decoration-muted-foreground",
			"neutral-subtle": "tw:bg-neutral-subtle tw:text-neutral-subtle-foreground tw:decoration-neutral-subtle"
		}
	}
});
// src/components/Badge/BadgeRoot.tsx
const BadgeRoot = ({ ref, variant, color, className, children, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	ref,
	"data-slot": "badge",
	className: cn(badgeRootVariants({
		variant,
		color,
		className
	})),
	children
});
// src/components/Badge/Badge.tsx
/**
* Inline pill for a status, count or tag.
*
* - Not for actions (`Button` / `ButtonInline`) or banners (`Alert`).
* - `Badge.Button` for a clickable badge.
*/
const BadgeInner = ({ ref, variant = "pill", color, prefix, prefixVariant = "inline", suffix, suffixVariant = "inline", className, labelClassName, ...labelProps }) => /* @__PURE__ */ jsxs(BadgeRoot, {
	className,
	variant,
	color,
	children: [
		prefix && /* @__PURE__ */ jsx(BadgeAddon, {
			align: "inline-start",
			variant: prefixVariant,
			children: prefix
		}),
		/* @__PURE__ */ jsx(BadgeLabel, {
			...labelProps,
			ref,
			className: labelClassName
		}),
		suffix && /* @__PURE__ */ jsx(BadgeAddon, {
			align: "inline-end",
			variant: suffixVariant,
			children: suffix
		})
	]
});
const Badge = Object.assign(BadgeInner, {
	Root: BadgeRoot,
	Button: BadgeButton,
	Label: BadgeLabel,
	Addon: BadgeAddon
});
export { Badge };

//# sourceMappingURL=Badge-CFRhducL.js.map